import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── helpers ────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

const PRICE_PRESETS = [49, 79, 99, 197, 297, 497];
const MILESTONES = [1000, 3000, 5000, 10000];

type Mode = 'snapshot' | 'growth';

interface MonthRow {
  month: number;
  totalClients: number;
  mrr: number;
  oneTime: number;
  total: number;
  cumulative: number;
}

function buildProjection(
  startClients: number,
  newPerMonth: number,
  monthlyPrice: number,
  oneTimeFee: number,
  churn: number,
): MonthRow[] {
  const rows: MonthRow[] = [];
  let totalClients = startClients;
  let cumulative = 0;
  for (let m = 1; m <= 12; m++) {
    totalClients = Math.max(0, totalClients + newPerMonth - churn);
    const mrr = totalClients * monthlyPrice;
    const oneTime = newPerMonth * oneTimeFee;
    const total = mrr + oneTime;
    cumulative += total;
    rows.push({ month: m, totalClients, mrr, oneTime, total, cumulative });
  }
  return rows;
}

// ── sub-components ─────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <motion.div
      layout
      className={`rounded-2xl border p-5 ${highlight ? 'bg-sage-600 border-sage-600' : 'bg-white border-gray-20'}`}
    >
      <p className={`text-xs font-semibold uppercase tracking-widest mb-1 ${highlight ? 'text-sage-100' : 'text-gray-40'}`}>
        {label}
      </p>
      <p className={`font-bold leading-none ${highlight ? 'text-white' : 'text-gray-80'}`} style={{ fontSize: 34 }}>
        {value}
      </p>
      {sub && (
        <p className={`text-xs mt-1.5 ${highlight ? 'text-sage-200' : 'text-gray-40'}`}>{sub}</p>
      )}
    </motion.div>
  );
}

function MilestoneCard({ goal, mrr, price }: { goal: number; mrr: number; price: number }) {
  const reached = mrr >= goal;
  const needed = Math.max(0, Math.ceil((goal - mrr) / Math.max(1, price)));
  return (
    <div
      className={`rounded-xl border p-4 text-center transition-colors ${
        reached ? 'border-sage-300 bg-sage-50' : 'border-gray-20 bg-white'
      }`}
    >
      <p className="font-bold text-gray-80" style={{ fontSize: 18 }}>{fmt(goal)}/mo</p>
      {reached ? (
        <p className="text-xs text-sage-600 font-semibold mt-1">Reached!</p>
      ) : (
        <p className="text-xs text-gray-40 mt-1">
          {needed} more client{needed !== 1 ? 's' : ''} at {fmt(price)}/mo
        </p>
      )}
    </div>
  );
}

function Slider({
  min, max, step, value, onChange,
}: { min: number; max: number; step: number; value: number; onChange: (v: number) => void }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative h-5 flex items-center">
      <div className="absolute w-full h-1.5 rounded-full bg-gray-20" />
      <div
        className="absolute h-1.5 rounded-full bg-sage-500 pointer-events-none"
        style={{ width: `${pct}%` }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="absolute w-full opacity-0 h-5 cursor-pointer"
        style={{ WebkitAppearance: 'none' }}
      />
      <div
        className="absolute w-5 h-5 rounded-full bg-sage-600 border-2 border-white shadow pointer-events-none"
        style={{ left: `calc(${pct}% - 10px)` }}
      />
    </div>
  );
}

function FeeInput({
  label,
  value,
  onChange,
}: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex-1">
      <label className="text-xs font-semibold text-gray-50 block mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-40 text-sm">$</span>
        <input
          type="number"
          min={0}
          placeholder="0"
          value={value || ''}
          onChange={e => onChange(Number(e.target.value) || 0)}
          className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-20 text-sm text-gray-80 bg-cream-50 focus:outline-none focus:ring-2 focus:ring-sage-300"
        />
      </div>
    </div>
  );
}

function PriceSelector({
  effectivePrice,
  price,
  customPrice,
  setPrice,
  setCustomPrice,
}: {
  effectivePrice: number;
  price: number;
  customPrice: string;
  setPrice: (v: number) => void;
  setCustomPrice: (v: string) => void;
}) {
  return (
    <div className="mb-6">
      <label className="text-sm font-semibold text-gray-60 block mb-2">
        Monthly price per client
      </label>
      <div className="flex flex-wrap gap-2 mb-3">
        {PRICE_PRESETS.map(p => (
          <button
            key={p}
            onClick={() => { setPrice(p); setCustomPrice(''); }}
            className="px-3 py-1.5 rounded-full text-sm font-semibold transition-all"
            style={{
              background: effectivePrice === p && customPrice === '' ? '#558347' : 'transparent',
              color: effectivePrice === p && customPrice === '' ? '#fff' : '#8B6347',
              border: effectivePrice === p && customPrice === '' ? 'none' : '1px solid #C5A795',
            }}
          >
            ${p}
          </button>
        ))}
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-40 font-semibold text-sm">$</span>
        <input
          type="number"
          min={0}
          placeholder="Custom price"
          value={customPrice}
          onChange={e => setCustomPrice(e.target.value)}
          className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-gray-20 text-sm text-gray-80 bg-cream-50 focus:outline-none focus:ring-2 focus:ring-sage-300"
        />
      </div>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────

export function RevenueCalculator() {
  const [mode, setMode] = useState<Mode>('snapshot');

  // shared price inputs
  const [price, setPrice] = useState(99);
  const [customPrice, setCustomPrice] = useState('');

  // snapshot-mode state
  const [snapClients, setSnapClients] = useState(5);
  const [snapSetupFee, setSnapSetupFee] = useState(0);
  const [snapPackageFee, setSnapPackageFee] = useState(0);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [scenarioPrice, setScenarioPrice] = useState(197);
  const [scenarioClients, setScenarioClients] = useState(10);

  // growth-mode state (fully independent)
  const [growthStartClients, setGrowthStartClients] = useState(0);
  const [newPerMonth, setNewPerMonth] = useState(5);
  const [churn, setChurn] = useState(0);
  const [growthProgrammeFee, setGrowthProgrammeFee] = useState(500);

  const effectivePrice = customPrice !== '' ? Number(customPrice) || 0 : price;

  // snapshot derived
  const snapMrr = snapClients * effectivePrice;
  const snapArr = snapMrr * 12;
  const snapFirstMonth = snapMrr + snapSetupFee + snapPackageFee;
  const scenarioMrr = scenarioClients * scenarioPrice;

  // growth derived
  const projection = useMemo(
    () => buildProjection(growthStartClients, newPerMonth, effectivePrice, growthProgrammeFee, churn),
    [growthStartClients, newPerMonth, effectivePrice, growthProgrammeFee, churn],
  );
  const lastRow = projection[projection.length - 1];
  const firstMilestoneRow = projection.find(r => r.mrr >= 1000);

  return (
    <div className="min-h-screen bg-cream-100" style={{ fontFamily: 'Urbanist, sans-serif' }}>

      {/* ── header ──────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-8 pt-7 pb-5">
        <img
          src="/human_potential_os_logo_V2_cropped.png"
          alt="Human Potential OS"
          className="h-10 object-contain object-left"
        />
        <span className="text-xs tracking-[0.2em] uppercase text-chocolate-400 font-medium hidden sm:block">
          Coaching Revenue Calculator
        </span>
      </header>
      <div className="mx-8 h-px bg-chocolate-200 opacity-40 mb-8" />

      {/* ── page title ──────────────────────────────────────────── */}
      <div className="px-8 mb-6">
        <p className="text-xs tracking-[0.2em] uppercase text-chocolate-400 font-medium mb-2">
          — Human Potential OS Foundations Certification —
        </p>
        <h1
          className="text-chocolate-900 leading-tight"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(24px, 3vw, 40px)',
            fontWeight: 500,
          }}
        >
          What Could Your Coaching Business Earn?
        </h1>
        <p className="text-chocolate-500 mt-2 text-sm max-w-xl">
          Enter your numbers below and watch your revenue potential update in real time.
        </p>
      </div>

      {/* ── mode tabs ───────────────────────────────────────────── */}
      <div className="px-8 mb-8">
        <div className="inline-flex rounded-xl border border-gray-20 bg-white p-1 shadow-soft gap-1">
          {([
            { key: 'snapshot', label: 'Snapshot', sub: 'Current numbers' },
            { key: 'growth', label: 'Growth Projection', sub: 'Month-by-month' },
          ] as { key: Mode; label: string; sub: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setMode(tab.key)}
              className="relative px-5 py-2.5 rounded-lg text-left transition-all"
              style={{
                background: mode === tab.key ? '#558347' : 'transparent',
              }}
            >
              <p className={`text-sm font-bold leading-tight ${mode === tab.key ? 'text-white' : 'text-gray-60'}`}>
                {tab.label}
              </p>
              <p className={`text-xs leading-tight mt-0.5 ${mode === tab.key ? 'text-sage-200' : 'text-gray-40'}`}>
                {tab.sub}
              </p>
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {mode === 'snapshot' ? (
          <motion.div
            key="snapshot"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* ── SNAPSHOT: body grid ─────────────────────────────── */}
            <div className="px-8 pb-16 flex flex-col lg:flex-row gap-6 items-start">

              {/* left: inputs */}
              <div className="w-full lg:max-w-md bg-white rounded-2xl border border-gray-20 p-6 shadow-soft flex-shrink-0">
                <h2 className="font-bold text-gray-80 mb-6" style={{ fontSize: 18 }}>Your Coaching Numbers</h2>

                {/* active clients */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-60">Active clients</label>
                    <span className="text-sm font-bold text-sage-600">{snapClients}</span>
                  </div>
                  <Slider min={1} max={50} step={1} value={snapClients} onChange={setSnapClients} />
                  <div className="flex justify-between text-xs text-gray-40 mt-1">
                    <span>1</span><span>50</span>
                  </div>
                </div>

                <PriceSelector
                  effectivePrice={effectivePrice}
                  price={price}
                  customPrice={customPrice}
                  setPrice={setPrice}
                  setCustomPrice={setCustomPrice}
                />

                <div className="flex gap-4">
                  <FeeInput label="Setup fee (optional)" value={snapSetupFee} onChange={setSnapSetupFee} />
                  <FeeInput label="Programme fee (optional)" value={snapPackageFee} onChange={setSnapPackageFee} />
                </div>
              </div>

              {/* right: results */}
              <div className="flex-1 min-w-0 flex flex-col gap-5">

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard label="Monthly Revenue" value={fmt(snapMrr)} sub="recurring each month" highlight />
                  <StatCard label="Annual Revenue" value={fmt(snapArr)} sub="over 12 months" />
                  <StatCard
                    label="First-Month Total"
                    value={fmt(snapFirstMonth)}
                    sub={snapSetupFee + snapPackageFee > 0 ? 'incl. one-time fees' : 'at current rate'}
                  />
                </div>

                <div className="bg-white rounded-2xl border border-gray-20 p-6 shadow-soft">
                  <h3 className="font-bold text-gray-80 mb-1" style={{ fontSize: 16 }}>Milestone Targets</h3>
                  <p className="text-xs text-gray-40 mb-4">How many clients you need at your current price</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {MILESTONES.map(m => (
                      <MilestoneCard key={m} goal={m} mrr={snapMrr} price={effectivePrice} />
                    ))}
                  </div>
                </div>

                {/* scenario comparison */}
                <div className="bg-white rounded-2xl border border-gray-20 shadow-soft overflow-hidden">
                  <button
                    onClick={() => setScenarioOpen(o => !o)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-cream-50 transition-colors"
                  >
                    <div>
                      <p className="font-bold text-gray-80" style={{ fontSize: 15 }}>What If I Raised My Price?</p>
                      <p className="text-xs text-gray-40 mt-0.5">Play with a different scenario to see the impact</p>
                    </div>
                    <span className="text-chocolate-400 text-lg font-light select-none">
                      {scenarioOpen ? '−' : '+'}
                    </span>
                  </button>

                  <AnimatePresence>
                    {scenarioOpen && (
                      <motion.div
                        key="scenario"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="px-6 pb-6 border-t border-gray-20">
                          <div className="flex flex-col sm:flex-row gap-6 mt-5">
                            <div className="flex-1 space-y-5">
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-sm font-semibold text-gray-60">Scenario clients</label>
                                  <span className="text-sm font-bold text-chocolate-500">{scenarioClients}</span>
                                </div>
                                <Slider min={1} max={50} step={1} value={scenarioClients} onChange={setScenarioClients} />
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-sm font-semibold text-gray-60">Scenario price / client</label>
                                  <span className="text-sm font-bold text-chocolate-500">{fmt(scenarioPrice)}/mo</span>
                                </div>
                                <Slider min={49} max={1000} step={1} value={scenarioPrice} onChange={setScenarioPrice} />
                                <div className="flex justify-between text-xs text-gray-40 mt-1">
                                  <span>$49</span><span>$1,000</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 flex flex-col gap-3">
                              <p className="text-xs font-semibold uppercase tracking-widest text-gray-40 mb-1">Side by Side</p>
                              <div className="flex gap-3">
                                <div className="flex-1 rounded-xl border border-gray-20 p-3 text-center">
                                  <p className="text-xs text-gray-40 mb-0.5">Current MRR</p>
                                  <p className="font-bold text-gray-80 text-lg">{fmt(snapMrr)}</p>
                                </div>
                                <div className="flex-1 rounded-xl border border-chocolate-200 bg-warm-50 p-3 text-center">
                                  <p className="text-xs text-chocolate-400 mb-0.5">Scenario MRR</p>
                                  <p className="font-bold text-chocolate-700 text-lg">{fmt(scenarioMrr)}</p>
                                </div>
                              </div>
                              <div className="flex gap-3">
                                <div className="flex-1 rounded-xl border border-gray-20 p-3 text-center">
                                  <p className="text-xs text-gray-40 mb-0.5">Current ARR</p>
                                  <p className="font-bold text-gray-80">{fmt(snapArr)}</p>
                                </div>
                                <div className="flex-1 rounded-xl border border-chocolate-200 bg-warm-50 p-3 text-center">
                                  <p className="text-xs text-chocolate-400 mb-0.5">Scenario ARR</p>
                                  <p className="font-bold text-chocolate-700">{fmt(scenarioMrr * 12)}</p>
                                </div>
                              </div>
                              {scenarioMrr > snapMrr && (
                                <div className="rounded-xl bg-sage-50 border border-sage-200 p-3 text-center">
                                  <p className="text-xs text-sage-700 font-semibold">
                                    +{fmt(scenarioMrr - snapMrr)}/mo · +{fmt((scenarioMrr - snapMrr) * 12)}/yr
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {effectivePrice > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-20 p-6 shadow-soft">
                    <p className="font-bold text-gray-80 mb-1" style={{ fontSize: 15 }}>
                      What 20 Clients Looks Like at Your Price
                    </p>
                    <p className="text-xs text-gray-40 mb-4">A common milestone for full-time coaches</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[5, 10, 15, 20].map(n => (
                        <div key={n} className={`rounded-xl border p-3 text-center ${n === snapClients ? 'border-sage-300 bg-sage-50' : 'border-gray-20'}`}>
                          <p className="text-xs text-gray-40 mb-1">{n} clients</p>
                          <p className="font-bold text-gray-80">{fmt(n * effectivePrice)}/mo</p>
                          <p className="text-xs text-gray-40">{fmt(n * effectivePrice * 12)}/yr</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

        ) : (
          <motion.div
            key="growth"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* ── GROWTH: body ────────────────────────────────────── */}
            <div className="px-8 pb-8 flex flex-col lg:flex-row gap-6 items-start">

              {/* left: inputs */}
              <div className="w-full lg:max-w-md bg-white rounded-2xl border border-gray-20 p-6 shadow-soft flex-shrink-0">
                <h2 className="font-bold text-gray-80 mb-1" style={{ fontSize: 18 }}>Growth Assumptions</h2>
                <p className="text-xs text-gray-40 mb-6">All inputs are independent of the Snapshot mode.</p>

                {/* clients today */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-60">Clients today (starting point)</label>
                    <span className="text-sm font-bold text-sage-600">{growthStartClients}</span>
                  </div>
                  <Slider min={0} max={50} step={1} value={growthStartClients} onChange={setGrowthStartClients} />
                  <div className="flex justify-between text-xs text-gray-40 mt-1">
                    <span>0</span><span>50</span>
                  </div>
                </div>

                {/* new clients per month */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-60">New clients per month</label>
                    <span className="text-sm font-bold text-sage-600">{newPerMonth}</span>
                  </div>
                  <Slider min={1} max={20} step={1} value={newPerMonth} onChange={setNewPerMonth} />
                  <div className="flex justify-between text-xs text-gray-40 mt-1">
                    <span>1</span><span>20</span>
                  </div>
                </div>

                {/* churn */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-60">Clients leaving per month</label>
                    <span className="text-sm font-bold text-chocolate-500">{churn}</span>
                  </div>
                  <Slider min={0} max={10} step={1} value={churn} onChange={setChurn} />
                  <div className="flex justify-between text-xs text-gray-40 mt-1">
                    <span>0</span><span>10</span>
                  </div>
                </div>

                <PriceSelector
                  effectivePrice={effectivePrice}
                  price={price}
                  customPrice={customPrice}
                  setPrice={setPrice}
                  setCustomPrice={setCustomPrice}
                />

                <div className="flex gap-4">
                  <FeeInput
                    label="Programme fee / new client"
                    value={growthProgrammeFee}
                    onChange={setGrowthProgrammeFee}
                  />
                </div>
              </div>

              {/* right: summary cards */}
              <div className="flex-1 min-w-0 flex flex-col gap-5">

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl border border-gray-20 p-5 shadow-soft">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-40 mb-1">MRR at Month 12</p>
                    <p className="font-bold text-gray-80" style={{ fontSize: 34 }}>{fmt(lastRow.mrr)}</p>
                    <p className="text-xs text-gray-40 mt-1.5">{lastRow.totalClients} active clients</p>
                  </div>
                  <div className="rounded-2xl border p-5 shadow-soft" style={{ background: '#558347', borderColor: '#558347' }}>
                    <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#c8e0bf' }}>Total Year 1 Revenue</p>
                    <p className="font-bold text-white" style={{ fontSize: 34 }}>{fmt(lastRow.cumulative)}</p>
                    <p className="text-xs mt-1.5" style={{ color: '#c8e0bf' }}>recurring + programme fees</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-20 p-5 shadow-soft">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-40 mb-1">Programme Fees Year 1</p>
                    <p className="font-bold text-gray-80" style={{ fontSize: 34 }}>{fmt(newPerMonth * growthProgrammeFee * 12)}</p>
                    <p className="text-xs text-gray-40 mt-1.5">{newPerMonth * 12} new clients × {fmt(growthProgrammeFee)}</p>
                  </div>
                </div>

                {firstMilestoneRow ? (
                  <div className="rounded-xl bg-sage-50 border border-sage-200 px-5 py-3 flex items-center gap-3">
                    <span className="text-sage-600 font-bold text-xl">&#10003;</span>
                    <p className="text-sm text-sage-700">
                      You hit <span className="font-bold">$1,000/mo recurring</span> in{' '}
                      <span className="font-bold">Month {firstMilestoneRow.month}</span> with{' '}
                      {firstMilestoneRow.totalClients} clients.
                    </p>
                  </div>
                ) : effectivePrice > 0 ? (
                  <div className="rounded-xl bg-warm-50 border border-chocolate-200 px-5 py-3 flex items-center gap-3">
                    <p className="text-sm text-chocolate-600">
                      At {fmt(effectivePrice)}/mo with +{newPerMonth} clients/month, you don't reach $1,000 MRR within 12 months.
                      Try increasing your price or new client rate.
                    </p>
                  </div>
                ) : null}

                {/* Milestone targets for growth mode */}
                {effectivePrice > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-20 p-6 shadow-soft">
                    <h3 className="font-bold text-gray-80 mb-1" style={{ fontSize: 16 }}>Month 12 Milestone Check</h3>
                    <p className="text-xs text-gray-40 mb-4">Where you'll land on key revenue targets by end of Year 1</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {MILESTONES.map(m => (
                        <MilestoneCard key={m} goal={m} mrr={lastRow.mrr} price={effectivePrice} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* full-width table */}
            {effectivePrice > 0 && (
              <div className="px-8 pb-16">
                <div className="bg-white rounded-2xl border border-gray-20 shadow-soft overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-20">
                    <h3 className="font-bold text-gray-80" style={{ fontSize: 16 }}>Month-by-Month Projection</h3>
                    <p className="text-xs text-gray-40 mt-0.5">
                      Starting with {growthStartClients} clients · +{newPerMonth}/mo · {fmt(effectivePrice)}/mo recurring
                      {growthProgrammeFee > 0 ? ` · ${fmt(growthProgrammeFee)} programme fee per new client` : ''}
                      {churn > 0 ? ` · ${churn} leaving/mo` : ''}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-20 bg-cream-50">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Month</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Total Clients</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Recurring</th>
                          {growthProgrammeFee > 0 && (
                            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Programme Fees</th>
                          )}
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Month Total</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Cumulative</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projection.map((row, i) => {
                          const isMilestone = firstMilestoneRow?.month === row.month;
                          return (
                            <tr
                              key={row.month}
                              className={`border-b border-gray-10 transition-colors ${isMilestone ? 'bg-sage-50' : i % 2 === 0 ? 'bg-white' : 'bg-cream-50'}`}
                            >
                              <td className="px-4 py-3 font-semibold text-gray-60">
                                Month {row.month}
                                {isMilestone && (
                                  <span className="ml-2 text-xs font-semibold text-sage-600 bg-sage-100 px-1.5 py-0.5 rounded-full">$1k</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right text-gray-60">{row.totalClients}</td>
                              <td className="px-4 py-3 text-right font-semibold text-gray-80">{fmt(row.mrr)}</td>
                              {growthProgrammeFee > 0 && (
                                <td className="px-4 py-3 text-right text-chocolate-500">{fmt(row.oneTime)}</td>
                              )}
                              <td className="px-4 py-3 text-right font-bold text-gray-80">{fmt(row.total)}</td>
                              <td className="px-4 py-3 text-right text-gray-50">{fmt(row.cumulative)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-20 bg-cream-100">
                          <td className="px-4 py-3 font-bold text-gray-80" colSpan={growthProgrammeFee > 0 ? 3 : 2}>Year 1 Total</td>
                          <td className="px-4 py-3 text-right font-bold text-gray-40">{lastRow.totalClients} clients</td>
                          {growthProgrammeFee > 0 && (
                            <td className="px-4 py-3 text-right font-bold text-chocolate-600">{fmt(newPerMonth * growthProgrammeFee * 12)}</td>
                          )}
                          <td className="px-4 py-3 text-right font-bold text-gray-80">{fmt(lastRow.cumulative)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── disclaimer ──────────────────────────────────────────── */}
      <div className="mx-8 mb-6 rounded-xl border border-gray-20 bg-cream-50 px-6 py-4">
        <p className="text-xs text-gray-40 leading-relaxed">
          <span className="font-semibold text-gray-50">Important:</span> This calculator is for illustration and planning purposes only. The figures shown are based on the numbers you enter and do not represent a guarantee of income, revenue, profit, client acquisition, or business results. Actual results depend on your offer, pricing, market, audience, sales activity, follow-up, delivery, client retention, and other factors.
        </p>
      </div>

      {/* ── footer CTA ──────────────────────────────────────────── */}
      <div className="mx-8 mb-10 rounded-2xl overflow-hidden" style={{ background: '#3d2b20' }}>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-8 py-7">
          <div>
            <p
              className="text-white font-semibold leading-snug"
              style={{ fontFamily: 'Georgia, serif', fontSize: 20 }}
            >
              Ready to build this business?
            </p>
            <p className="text-warm-300 text-sm mt-1">
              The Human Potential OS gives you the tools, systems, and community to make these numbers real.
            </p>
          </div>
          <a
            href="https://humanpotentialos.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 px-6 py-3 rounded-full text-sm font-semibold text-chocolate-900 bg-warm-400 hover:bg-warm-500 transition-colors"
          >
            humanpotentialos.com
          </a>
        </div>
      </div>
    </div>
  );
}
